/**
 * Wave 6 S3 -- useIdleTimer hook.
 *
 * Tracks DOM activity (mousemove / keydown / click / scroll / touch) and
 * fires ``onIdle`` when no event arrives within ``idleMs``. A separate
 * ``onWarning`` callback fires ``warnMs`` before the idle threshold.
 *
 * The hook is debounced via a single timeout: each activity event resets
 * it, so the cost is one setTimeout per burst regardless of event volume.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'click',
]

export interface UseIdleTimerOptions {
  idleMinutes?: number
  warnMinutes?: number
  onIdle?: () => void
  onWarning?: () => void
  enabled?: boolean
}

export interface UseIdleTimerResult {
  isIdle: boolean
  isWarning: boolean
  reset: () => void
  msUntilIdle: number
}

export function useIdleTimer(opts: UseIdleTimerOptions): UseIdleTimerResult {
  const {
    idleMinutes = 15,
    warnMinutes = 1,
    onIdle,
    onWarning,
    enabled = true,
  } = opts

  const idleMs = idleMinutes * 60 * 1000
  const warnMs = warnMinutes * 60 * 1000

  const [isIdle, setIsIdle] = useState(false)
  const [isWarning, setIsWarning] = useState(false)
  const [msUntilIdle, setMsUntilIdle] = useState(idleMs)

  const lastActivityRef = useRef<number>(Date.now())
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const warnTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const tickTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const clearTimers = () => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
    if (warnTimerRef.current) clearTimeout(warnTimerRef.current)
    idleTimerRef.current = null
    warnTimerRef.current = null
  }

  const reset = useCallback(() => {
    lastActivityRef.current = Date.now()
    setIsIdle(false)
    setIsWarning(false)
    setMsUntilIdle(idleMs)
    clearTimers()
    if (!enabled) return

    warnTimerRef.current = setTimeout(() => {
      setIsWarning(true)
      if (onWarning) onWarning()
    }, Math.max(0, idleMs - warnMs))

    idleTimerRef.current = setTimeout(() => {
      setIsIdle(true)
      setIsWarning(false)
      if (onIdle) onIdle()
    }, idleMs)
  }, [idleMs, warnMs, enabled, onIdle, onWarning])

  useEffect(() => {
    if (!enabled) {
      clearTimers()
      return
    }

    const handler = () => reset()
    ACTIVITY_EVENTS.forEach((evt) =>
      window.addEventListener(evt, handler, { passive: true }),
    )

    // Keep a ticking countdown for UI display (cheap; 1s interval).
    tickTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current
      setMsUntilIdle(Math.max(0, idleMs - elapsed))
    }, 1000)

    reset()

    return () => {
      ACTIVITY_EVENTS.forEach((evt) =>
        window.removeEventListener(evt, handler),
      )
      if (tickTimerRef.current) clearInterval(tickTimerRef.current)
      clearTimers()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, idleMs, warnMs])

  return { isIdle, isWarning, reset, msUntilIdle }
}

export default useIdleTimer
